/**
 * Shared readers for RFC 7807 `application/problem+json` response bodies —
 * the API's error format (mirrors `apps/api/src/lib/problem.ts` server-side).
 *
 * Every API client that surfaces a server-authored error message needs the
 * same three things: parse the body as JSON without throwing on a body that
 * isn't JSON (or isn't there at all), read the field(s) that carry the
 * sentence, and fall back to a generic message keyed off the HTTP status
 * when none of that is available. That logic was copy-pasted (with small,
 * genuine divergences) across `evaluations.ts`, `opportunities.ts` and
 * `professional-errors.ts` — this module is the single place it lives now.
 */

/**
 * Parses a response body as a JSON object, tolerating a missing, malformed,
 * or non-object body (including the literal JSON `null`). Returns `null`
 * rather than throwing — a failed request's body is never guaranteed to be
 * valid JSON (proxy error pages, network truncation, etc.), and every
 * problem+json reader in this codebase needs that same guarantee.
 */
export async function parseProblemJson(res: Response): Promise<Record<string, unknown> | null> {
  let parsed: unknown
  try {
    parsed = await res.json()
  } catch {
    return null
  }
  return typeof parsed === 'object' && parsed !== null ? (parsed as Record<string, unknown>) : null
}

/**
 * Lê um membro de extensão de um problem+json. O `problemJson` do servidor
 * espalha as `extensions` na RAIZ do corpo, mas o RFC 7807 também as admite
 * aninhadas — a raiz primeiro, `extensions` depois, como os leitores das
 * contagens de vínculo (`readLinkedCount`) já fazem.
 */
export function readProblemExtension(body: Record<string, unknown>, key: string): unknown {
  if (key in body) return body[key]
  const nested = body.extensions
  if (typeof nested === 'object' && nested !== null) {
    return (nested as Record<string, unknown>)[key]
  }
  return undefined
}

/**
 * O registro que já existe num 409 de duplicidade — `{ id, name, status }`, ou
 * `null` quando falta qualquer um ou o estado não é do enum. Um objeto pela
 * metade mentiria ao tipo, e o cadastro rápido decide o «Usar este» por ele.
 */
export interface ExistingRecord {
  id: string
  name: string
  status: 'active' | 'inactive'
}

export function toExistingRecord(value: unknown): ExistingRecord | null {
  if (typeof value !== 'object' || value === null) return null
  const { id, name, status } = value as Record<string, unknown>
  if (typeof id !== 'string' || id === '' || typeof name !== 'string' || name === '') return null
  if (status !== 'active' && status !== 'inactive') return null
  return { id, name, status }
}

/**
 * Reads a user-facing message off a problem+json body, trying each field in
 * `fields` order (first non-empty string wins) and falling back to
 * `fallback` when the body is unreadable or none of the fields hold one.
 * Never throws.
 *
 * `fields` defaults to `['detail']` because that is this codebase's dominant
 * convention (`apps/api/src/routes/evaluations.ts` puts the PT-BR sentence in
 * `detail`, keeps `title` an English constant). The opportunity-document
 * endpoints are the one place that inverts it — `title` carries the friendly
 * sentence there — so those call sites pass `['title', 'detail']` explicitly
 * rather than silently getting the wrong field.
 */
export async function readProblemMessage(
  res: Response,
  fallback: string,
  fields: ReadonlyArray<'title' | 'detail'> = ['detail'],
): Promise<string> {
  const body = await parseProblemJson(res)
  if (body) {
    for (const field of fields) {
      const value = body[field]
      if (typeof value === 'string' && value.length > 0) return value
    }
  }
  return fallback
}
